import { useAuth0 } from "@auth0/auth0-react";
import { usePublishedMediaInsightsDcrActionsQuery } from "@decentriq/graphql/dist/hooks";
import { DataRoomKind } from "@decentriq/graphql/dist/types";
import {
  faBan,
  faGaugeMax,
  faLink,
  faStar,
} from "@fortawesome/pro-light-svg-icons";
import { faStar as fasStar } from "@fortawesome/pro-solid-svg-icons";
import { IconButton } from "@mui/joy";
import { useBoolean } from "ahooks";
import { memo, useCallback, useMemo } from "react";
import { Actions, DataRoomStopDialog } from "components";
import { DataRoomActionTypes } from "features/dataRoom";
import PerformanceReportDownloadDialog from "features/mediaDataRoom/components/PerformanceReportDownloadDialog/PerformanceReportDownloadDialog";
import {
  MediaDataRoomContext,
  MediaDataRoomInsightsDataContext,
} from "features/mediaDataRoom/contexts";
import { PERFORMANCE_REPORT_ALLOWED_USERS } from "features/mediaDataRoom/models";
import {
  usePublishedDataRoomCopyLink,
  usePublishedDataRoomStop,
} from "features/publishedDataRoom";
import { usePublishedDataRoomIsFavorite } from "features/publishedDataRoom/components/PublishedDataRoomActions/hooks";
import {
  mapErrorToGeneralSnackbar,
  mapMediaDataRoomErrorToSnackbar,
  useDataRoomSnackbar,
  useSafeContext,
} from "hooks";
import { DataRoomTypeNames } from "models";

export enum MediaDataRoomActionTypes {
  ViewConfiguration,
  PerformanceReport,
}

interface MediaDataRoomActionsProps {
  id: string;
  withPerformanceReport?: boolean;
  [key: string]: any;
}

const MediaDataRoomActions = memo<MediaDataRoomActionsProps>(
  ({
    id,
    withPerformanceReport,
    actions: actionsFunc = (actions: any) => actions,
    inline = false,
    moreIcon,
  }) => {
    const { user: { email: currentUserEmail = "" } = {} } = useAuth0();
    const { enqueueSnackbar } = useDataRoomSnackbar();
    const [
      isPerformanceReportDownloadDialogOpen,
      {
        setTrue: openPerformanceReportDownloadDialog,
        setFalse: closePerformanceReportDownloadDialog,
      },
    ] = useBoolean(false);
    const { data, loading: isDataRoomLoading } =
      usePublishedMediaInsightsDcrActionsQuery({
        variables: {
          id,
        },
      });
    const name = data?.publishedMediaInsightsDcr?.name;
    const isStopped = data?.publishedMediaInsightsDcr?.isStopped;
    const driverAttestationHash =
      data?.publishedMediaInsightsDcr?.driverAttestationHash;
    const dataRoomKind = data?.publishedMediaInsightsDcr.kind;

    const publishedMediaDcr = useSafeContext(MediaDataRoomContext, true);
    const mediaDcrData = useSafeContext(MediaDataRoomInsightsDataContext, true);

    const hideStopButton = useMemo(() => {
      return (
        isStopped ||
        !publishedMediaDcr ||
        (dataRoomKind !== DataRoomKind.MediaV3 &&
          !publishedMediaDcr.supportedFeatures?.canStopMediaDataRoom)
      );
    }, [dataRoomKind, isStopped, publishedMediaDcr]);

    const hidePerformanceReportButton = useMemo(() => {
      return (
        !withPerformanceReport ||
        !PERFORMANCE_REPORT_ALLOWED_USERS.includes(currentUserEmail) ||
        !mediaDcrData?.datasets?.data?.hasRequiredData ||
        isStopped ||
        !publishedMediaDcr?.supportedFeatures?.performanceReportEnabled ||
        !publishedMediaDcr?.supportedFeatures?.enableLookalike
      );
    }, [
      currentUserEmail,
      withPerformanceReport,
      mediaDcrData?.datasets?.data?.hasRequiredData,
      isStopped,
      publishedMediaDcr?.supportedFeatures?.performanceReportEnabled,
      publishedMediaDcr?.supportedFeatures?.enableLookalike,
    ]);
    // Copy link to data clean room
    const { publishedDataRoomCopyLink } = usePublishedDataRoomCopyLink({
      __typename: DataRoomTypeNames.PublishedMediaInsightsDcr,
      id,
    });

    const dataRoomCopyLink = useCallback(
      () =>
        publishedDataRoomCopyLink().catch((error) => {
          enqueueSnackbar(
            ...mapMediaDataRoomErrorToSnackbar(
              error,
              "Data clean room link could not be generated."
            )
          );
        }),
      [publishedDataRoomCopyLink, enqueueSnackbar]
    );
    // Favorite/unfavorite data clean room
    const {
      publishedDataRoomIsFavorite: isFavorite,
      publishedDataRoomIsFavoriteLoading: isDataRoomIsFavoriteLoading,
      setPublishedDataRoomIsFavorite: setIsFavorite,
      setPublishedDataRoomIsFavoriteLoading: isSetDataRoomIsFavoriteLoading,
    } = usePublishedDataRoomIsFavorite({
      __typename: DataRoomTypeNames.PublishedMediaInsightsDcr,
      id,
      skip: isDataRoomLoading,
    });
    const toggleDataRoomIsFavorite = () =>
      setIsFavorite(!isFavorite).catch((error) => {
        enqueueSnackbar(
          ...mapErrorToGeneralSnackbar(
            error,
            `Data clean room could not be ${
              !isFavorite ? "added to" : "removed from"
            } Favorites`
          )
        );
      });
    // Stop data clean room
    const [
      isDataRoomStopDialogOpen,
      { setTrue: openDataRoomStopDialog, setFalse: closeDataRoomStopDialog },
    ] = useBoolean(false);
    const { isDataRoomStopLoading, stopDataRoomMutation: dataRoomStop } =
      usePublishedDataRoomStop({
        __typename: DataRoomTypeNames.PublishedMediaInsightsDcr,
        dataRoomId: id,
        dataRoomKind,
        driverAttestationHash,
        onSettled: () => closeDataRoomStopDialog(),
      });

    const actions = actionsFunc({
      buttons: [
        {
          component: IconButton,
          disabled: isDataRoomLoading || isDataRoomIsFavoriteLoading,
          hidden: false,
          icon: isFavorite ? fasStar : faStar,
          loading:
            isDataRoomIsFavoriteLoading || isSetDataRoomIsFavoriteLoading,
          onClick: toggleDataRoomIsFavorite,
          square: true,
          tooltipPlacement: "bottom-end",
          tooltipTitle: isFavorite
            ? "Remove from Favorites"
            : "Add to Favorites",
          type: DataRoomActionTypes.ToggleFavorite,
        },
      ],
      menuLists: [
        [
          {
            disabled: isDataRoomLoading || isDataRoomIsFavoriteLoading,
            hidden: false,
            icon: isFavorite ? fasStar : faStar,
            loading:
              isDataRoomIsFavoriteLoading || isSetDataRoomIsFavoriteLoading,
            name: isFavorite ? "Remove from Favorites" : "Add to Favorites",
            onClick: toggleDataRoomIsFavorite,
            type: DataRoomActionTypes.ToggleFavorite,
          },
          {
            disabled: false,
            icon: faLink,
            name: "Copy link",
            onClick: dataRoomCopyLink,
            type: DataRoomActionTypes.CopyLink,
          },
          {
            hidden: hidePerformanceReportButton,
            icon: faGaugeMax,
            name: "Performance report",
            onClick: openPerformanceReportDownloadDialog,
            type: MediaDataRoomActionTypes.PerformanceReport,
          },
          {
            disabled: false,
            hidden: hideStopButton,
            icon: faBan,
            isRed: true,
            loading: false,
            name: "Stop data clean room",
            onClick: openDataRoomStopDialog,
            type: DataRoomActionTypes.Stop,
          },
        ],
      ],
    });
    return (
      <Actions
        actions={actions}
        inline={inline}
        moreIcon={moreIcon}
        moreTooltipTitle=""
      >
        <DataRoomStopDialog
          loading={isDataRoomStopLoading}
          name={name}
          onCancel={closeDataRoomStopDialog}
          onConfirm={dataRoomStop}
          open={isDataRoomStopDialogOpen}
        />
        {withPerformanceReport && (
          <PerformanceReportDownloadDialog
            dataRoomId={id}
            onCancel={closePerformanceReportDownloadDialog}
            open={isPerformanceReportDownloadDialogOpen}
          />
        )}
      </Actions>
    );
  }
);
MediaDataRoomActions.displayName = "MediaDataRoomActions";

export default MediaDataRoomActions;
